import { useIsProjectsEnabled } from '@/features/flags';
import { trackWorkArtifactCreated } from '@/models/analytics/features/work';
import { resolveExportDocumentFilename } from '@/utils/fileHelpers';
import { logger } from '@/utils/logger/logger';
import { useSaveExportAsArtefact } from '@heidi/agents';
import {
  presentationAtom,
  presentationTitleEditingAtom,
  selectedSlideIndexAtom,
  usePresentationHistory,
} from '@heidi/agents/data';
import { sanitizeError } from '@heidi/runtime/logger';
import { useAtom, useAtomValue } from 'jotai';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { useIntl } from 'react-intl';

import { exportPresentation, type ExportFormat } from './hooks/presentationApi';
import { slideBuilderMessages } from './slide-builder.messages';

export function useSlideUndoRedoHotkeys(undo: () => void, redo: () => void) {
  useEffect(() => {
    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);
}

export function useSlideToolbarActions() {
  const intl = useIntl();
  const isProjectsEnabled = useIsProjectsEnabled();
  const { save, isSaving: isSavingArtefact } = useSaveExportAsArtefact(
    trackWorkArtifactCreated
  );
  const presentation = useAtomValue(presentationAtom);
  const [selectedIndex] = useAtom(selectedSlideIndexAtom);
  const [isEditingTitle, setIsEditingTitle] = useAtom(presentationTitleEditingAtom);
  const [titleDraft, setTitleDraft] = useState('');
  const titleInputRef = useRef<HTMLInputElement>(null);

  const { setWithHistory, undo, redo, canUndo, canRedo } = usePresentationHistory();

  const totalSlides = presentation?.slides.length ?? 0;

  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  const beginRename = useCallback(() => {
    setTitleDraft(presentation?.title ?? '');
    setIsEditingTitle(true);
  }, [presentation?.title, setIsEditingTitle]);

  const commitTitle = useCallback(() => {
    if (!presentation) return;
    setWithHistory({ ...presentation, title: titleDraft.trim() });
    setIsEditingTitle(false);
  }, [presentation, titleDraft, setWithHistory, setIsEditingTitle]);

  const handleTitleKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') commitTitle();
      if (e.key === 'Escape') setIsEditingTitle(false);
    },
    [commitTitle, setIsEditingTitle]
  );

  const [exportingFormat, setExportingFormat] = useState<ExportFormat | null>(null);
  const isExporting = exportingFormat !== null || isSavingArtefact;

  const displayTitle = presentation?.title?.trim()
    ? presentation.title.trim()
    : intl.formatMessage(slideBuilderMessages.untitledPresentation);

  const slideCounter =
    totalSlides > 0
      ? intl.formatMessage(slideBuilderMessages.slideCount, {
          current: selectedIndex + 1,
          total: totalSlides,
        })
      : undefined;

  const handleExport = useCallback(
    async (format: ExportFormat) => {
      if (!presentation?.id) return;
      setExportingFormat(format);
      try {
        const { download_url } = await exportPresentation(presentation.id, format);
        window.open(download_url, '_blank', 'noopener,noreferrer');
      } catch (err) {
        logger.error('slide_export_failed', {
          format,
          error: sanitizeError(err),
        });
      } finally {
        setExportingFormat(null);
      }
    },
    [presentation?.id]
  );

  const handleSaveAsArtefact = useCallback(async () => {
    if (!presentation?.id) return;
    setExportingFormat('pptx');
    try {
      const { download_url } = await exportPresentation(presentation.id, 'pptx');
      const filename = resolveExportDocumentFilename({
        url: download_url,
        title: displayTitle,
        filename: `${presentation.title?.trim() || 'presentation'}.pptx`,
      });
      await save({ url: download_url, filename });
    } catch (err) {
      logger.error('slide_save_as_artefact_failed', {
        error: sanitizeError(err),
      });
    } finally {
      setExportingFormat(null);
    }
  }, [displayTitle, presentation?.id, presentation?.title, save]);

  return {
    presentation,
    displayTitle,
    slideCounter,
    totalSlides,
    selectedIndex,
    isEditingTitle,
    titleDraft,
    setTitleDraft,
    titleInputRef,
    beginRename,
    commitTitle,
    handleTitleKeyDown,
    undo,
    redo,
    canUndo,
    canRedo,
    handleExport,
    handleSaveAsArtefact,
    exportingFormat,
    isExporting,
    isProjectsEnabled,
  };
}
